import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {EnergyCommunityNetwork} from '../../shared/entities';
import {SmagApiService} from '../../api/smag-api.service';
import {Router} from '@angular/router';
import {MsgBoxService} from '../../shared/msg-box.service';
import {I18NService} from '../../i18n/i18n.service';
import {catchError, first, map, switchMap, takeUntil} from 'rxjs/operators';
import {
    ACCUMULATOR_ABSORBED_ENERGY_DEVICE,
    ACCUMULATOR_AVERAGE_ABSORBED_POWER_DEVICE,
    ACCUMULATOR_AVERAGE_INJECTED_POWER_DEVICE,
    ACCUMULATOR_INJECTED_ENERGY_DEVICE,
    ACCUMULATOR_STATE_OF_CHARGE_DEVICE,
    ECMember,
    ECMemberParticipationType,
    ECMode,
    ECRepresentative,
    EnergyCommunity,
    EnergyCommunityNamedPod,
    Entity,
    ENTITY_METADATA,
    EntitySubType,
    EntityType,
    Plant,
    PLANT_AVERAGE_PRODUCED_POWER_DEVICE,
    PLANT_PRODUCED_ENERGY_DEVICE,
    PlantStatisticalProfile,
    Pod,
    POD_ABSORBED_ENERGY_DEVICE,
    POD_AVERAGE_ABSORBED_POWER_DEVICE,
    POD_AVERAGE_INJECTED_POWER_DEVICE,
    POD_INJECTED_ENERGY_DEVICE,
    PodStatisticalProfile,
    UpdateEnergyCommunityRequest
} from 'smag-commons/cfg/hierarchy';
import {can} from '../../shared/permissions';
import {forkJoin, Observable, of, Subject, throwError} from 'rxjs';
import {NetworkService} from '../../shared/network.service';
import {ECNetwork, ECSelection} from '../../shared/ec-resource-tree/ec-resource-tree.component';
import {
    EntityMeasureAggregationType,
    EntityNetworkType as SmagEntityNetworkType,
    MeasureAggregationDataSource,
    Permission,
    Scope,
    TIME_UNITS,
    TimeAggregationType
} from 'smag-commons/dto/api';
import {hasFieldEntityDevice} from '../../shared/common';
import * as _ from 'lodash';
import {ECAggregation} from '../../shared/ec-measure-aggregations/ec-measure-aggregations.component';
import {errorCode, getStatisticalProfileTotalsFor, isAdvancedUser, isDefined, safeRatio} from '../../commons/commons';
import {isConsumer, isProducer, isProsumer} from 'smag-commons/fcn';
import {PlanningNetwork} from '../../shared/plannings/plannings.component';
import {ForecastResource} from '../../shared/forecasts/forecasts.component';
import {ConfigurationService} from '../../shared/configuration.service';
import {EconomicsConfig, Phases, VoltageLevel} from 'smag-commons/cfg';
import {EnergyProfiles, mkMemberName, mkMode, POD_PHASES, POD_VOLTAGES} from '../../shared/energy-community';
import {ErrorCode} from '../../api/errors';
import {AuthService} from 'auth-commons';
import {DialogService} from '../../shared/dialog/dialog.service';
import {EntityNicePathPipe, RadioRateModePipe, RateModePipe, UserTypePipe} from '../../shared/shared.pipe';
import {PodCreatorEnergyCommunity} from './ec-pod-creator-wizard/ec-pod-creator-wizard.component';
import {ModelType} from 'smag-commons/cfg/prediction';
import {TurboObjectWithDates} from '../../api/dto';
import {STATISTICS_ERROR_PLANT_KEY, StatisticsError, StatisticsErrorType} from 'smag-commons/dto/api/statistics-errors';
import {getMemberTargetPodRelations, getPodCurrentHolderAndOwner} from '../../shared/ec-members';

interface Resource extends HierarchyEntity {
    data: TurboObjectWithDates<Entity>;
    members: ECMember[];
    holder?: ECMember;
    owner?: ECMember;
}

interface LazyRepresentative {
    representative?: ECRepresentative;
    name?: string;
    show: boolean;
}

interface Statistics {
    pods: number;
    plants: number;
}

const AGGREGATION_REFRESH_FREQUENCY = 60; // seconds

@Component({
    selector: 'app-ec-resources',
    templateUrl: './ec-resources.component.html',
    styleUrls: ['./ec-resources.component.scss']
})
export class ECResourcesComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private readonly utp: UserTypePipe;
    private readonly rmp: RateModePipe;
    private readonly npp: EntityNicePathPipe;

    @ViewChild('deletePod') private deletePodDialog: TemplateRef<any>;
    @ViewChild('createPod') private podCreatorDialog: TemplateRef<any>;
    @ViewChild('editPod') private podEditorDialog: TemplateRef<any>;
    @ViewChild('editCommunity') private communityEditorDialog: TemplateRef<any>;
    @ViewChild('deleteCommunity') private deleteCommunityDialog: TemplateRef<any>;
    @ViewChild('showPods') private showPodsDialog: TemplateRef<any>;

    ecNetwork: ECNetwork | null = null;
    planningNetwork: PlanningNetwork | null = null;
    forecastResource: ForecastResource | null = null;
    resource: Resource | null = null; // currently selected resource
    aggregations: ECAggregation[] = [];
    representative: LazyRepresentative = null;
    statistics: Statistics = {pods: 0, plants: 0};
    window = window;
    isAdvUser: Observable<boolean>;
    plantsErrors: StatisticsError[] = [];
    plantsErrorsMap: { [plantName: string]: StatisticsErrorType } = {};
    ecMode: ECMode = null;

    // exported in HTML
    readonly AGGREGATION_REFRESH_FREQUENCY = AGGREGATION_REFRESH_FREQUENCY;
    readonly ECMode = ECMode;
    readonly EnergyProfiles = EnergyProfiles;
    readonly EntityType = EntityType;
    readonly ECMemberParticipationType = ECMemberParticipationType;
    readonly isDefined = isDefined;
    readonly mkMemberName = mkMemberName;

    constructor(private readonly apiService: SmagApiService,
                private readonly router: Router,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly hierarchyService: HierarchyService,
                private readonly networkService: NetworkService,
                private readonly configurationService: ConfigurationService,
                private readonly auth: AuthService,
                private readonly dialogService: DialogService) {
        this.npp = new EntityNicePathPipe();
        this.rmp = new RadioRateModePipe();
        this.utp = new UserTypePipe();
    }

    ngOnInit() {
        this.hierarchyService.selection.pipe(
            switchMap(resource => !resource || resource.type === EntityType.ENERGY_COMMUNITY ?
                of([resource?.id, resource?.id]) : // either no resource or an energy community is selected
                this.getResourceCommunityId(resource.id).pipe( // something that *could* be part of an energy community is selected
                    map(networkId => networkId ? [networkId, resource.id] : [null, null])
                )),
            takeUntil(this.destroyed)
        ).subscribe(([networkId, resourceId]) => {
            this.loadRepresentative(networkId);
            this.loadNetwork(networkId, resourceId, false)
                .then(() => this.loadResource(resourceId));
            if (networkId) {
                this.apiService.communityPodsStatistics(networkId)
                    .subscribe(result => this.ecMode = mkMode(result));

                this.apiService.communityPlantsStatistics(networkId)
                    .subscribe(result => {
                        this.plantsErrors = result.errors.filter(error => error.type === StatisticsErrorType.MISSING_PLANT_EFFECTIVE_DATE);
                        this.plantsErrorsMap = this.plantsErrors
                            .reduce((errorsMap, error) => ({
                                ...errorsMap,
                                [error.metadata[STATISTICS_ERROR_PLANT_KEY]]: error.type
                            }), {});
                    });
            }
        });
        this.isAdvUser = isAdvancedUser(of(this.auth.profile));
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get communitySelected(): boolean {
        return this.resource.type === EntityType.ENERGY_COMMUNITY;
    }

    get plannableResource(): boolean {
        return this.resource.type === EntityType.ENERGY_COMMUNITY;
    }

    get predictableResource(): boolean {
        return this.resource.type === EntityType.POD || this.resource.type === EntityType.PLANT;
    }

    get podResource(): TurboObjectWithDates<Pod> {
        return this.resource.type === EntityType.POD ? this.resource.data as TurboObjectWithDates<Pod> : null;
    }

    get plantResource(): TurboObjectWithDates<Plant> {
        return this.resource.type === EntityType.PLANT ? this.resource.data as TurboObjectWithDates<Plant> : null;
    }

    get canReadCommunity(): boolean {
        return can(this.ecNetwork.network.root.permissions, Permission.READ);
    }

    get canCreateResource(): boolean {
        // check that we are able to `CREATE` resources under the currently selected resource
        return can(this.resource.permissions, Permission.CREATE);
    }

    get canUpdateResource(): boolean {
        // check that we are able to `UPDATE` the currently selected resource
        return can(this.resource.permissions, Permission.UPDATE);
    }

    get canDeleteResource(): boolean {
        // check that we are able to `DELETE` the currently selected resource
        return can(this.resource.permissions, Permission.DELETE);
    }

    get podCode(): string {
        return (this.resource.data.metadata || {})[ENTITY_METADATA.POD_KEY];
    }

    get podEnergyProfile(): EnergyProfiles {
        if (this.resource.type !== EntityType.POD) {
            return null;
        }
        const ecPod = this.ecNetwork.network.pods[this.resource.id];
        if (isConsumer(ecPod)) {
            return EnergyProfiles.CONSUMER;
        }
        if (isProsumer(ecPod)) {
            return EnergyProfiles.PROSUMER;
        }
        if (isProducer(ecPod)) {
            return EnergyProfiles.PRODUCER;
        }
        return null;
    }

    get statisticalProfileInfo(): string {
        if (this.resource.type === EntityType.PLANT) {
            return this.i18n.translate(this.resource.sub_type === EntitySubType.WIND_PLANT ? 'Wind plant' : 'Photovoltaic plant') + ', '
                + this.i18n.translate(this.rmp.transform((this.resource.data as Plant).statistical_profile.rate_mode)).toLowerCase();
        } else if (this.resource.type === EntityType.POD) {
            const pod = this.resource.data as Pod;
            const type = this.i18n.translate(this.podEnergyProfile as string);
            return pod.statistical_profile.user_type ?
                type + ', ' + this.i18n.translate(this.utp.transform(pod.statistical_profile.user_type)).toLowerCase()
                + ', ' + this.i18n.translate(this.rmp.transform(pod.statistical_profile.rate_mode)).toLowerCase() :
                type + ', ' + this.i18n.translate(this.rmp.transform(pod.statistical_profile.rate_mode)).toLowerCase();
        }
    }

    get predictiveModelType(): ModelType {
        // Finds either the first preferred or first non-preferred
        const temp = this.resource.data.predictive_models || [];
        return temp.find(m => m.preferred)?.type || (temp.find(m => !m.preferred)?.type || null);
    }

    get monitoredPod(): boolean {
        const ecPod = this.ecNetwork.network.pods[this.resource.id];
        if (!ecPod) {
            return false;
        }
        if (isConsumer(ecPod)) {
            return hasFieldEntityDevice(this.resource.data, POD_ABSORBED_ENERGY_DEVICE) ||
                hasFieldEntityDevice(this.resource.data, POD_AVERAGE_ABSORBED_POWER_DEVICE);
        } else if (isProsumer(ecPod)) {
            return (hasFieldEntityDevice(this.resource.data, POD_ABSORBED_ENERGY_DEVICE) ||
                    hasFieldEntityDevice(this.resource.data, POD_AVERAGE_ABSORBED_POWER_DEVICE)) &&
                (hasFieldEntityDevice(this.resource.data, POD_INJECTED_ENERGY_DEVICE) ||
                    hasFieldEntityDevice(this.resource.data, POD_AVERAGE_INJECTED_POWER_DEVICE));
        } else if (isProducer(ecPod)) {
            return hasFieldEntityDevice(this.resource.data, POD_INJECTED_ENERGY_DEVICE) ||
                hasFieldEntityDevice(this.resource.data, POD_AVERAGE_INJECTED_POWER_DEVICE);
        }
        return false;
    }

    get communityUpdatable(): boolean {
        return this.communitySelected && can(this.resource.permissions, Permission.UPDATE);
    }

    get communityDeletable(): boolean {
        return this.communitySelected && can(this.resource.permissions, Permission.DELETE);
    }

    get relevantMember(): ECMember | null {
        // the relevant member is the current holder of the POD or, if none, its owner:
        return this.resource.holder || this.resource.owner || null;
    }

    onSelectResource(selection: ECSelection) {
        if (selection.resourceId !== this.resource?.id) {
            this.hierarchyService.select(selection.resourceId);
        }
    }

    onClickDeletePod() {
        this.dialogService.open<any, 'cancel' | 'delete' | 'members'>({
            title: this.i18n.translate('Are you sure you want to delete {0}?', this.resource.name),
            template: this.deletePodDialog
        }).closed.then(cmd => {
            if (cmd === 'delete') {
                this.deletePod().then();
            } else if (cmd === 'members') {
                this.onClickGotoMember();
            }
        });
    }

    onClickCreatePodNew() {
        this.dialogService.open<any, any>({
            template: this.podCreatorDialog,
            context: {
                ec: {
                    id: this.ecNetwork.network.root.id,
                    ...(this.ecNetwork.network.rootEntity as EnergyCommunity)
                } as PodCreatorEnergyCommunity
            }
        });
    }

    onClickEditPod() {
        this.apiService.getECPod(this.ecNetwork.network.root.id, this.resource.id)
            .subscribe(ecPod => {
                this.dialogService.open<any, any>({
                    template: this.podEditorDialog,
                    context: {
                        ec: {
                            id: this.ecNetwork.network.root.id,
                            ...(this.ecNetwork.network.rootEntity as EnergyCommunity)
                        } as PodCreatorEnergyCommunity,
                        pod: ecPod,
                        member: this.relevantMember
                    }
                });
            });
    }

    onClickGotoMember() {
        this.router.navigate(['/c', 'ec', 'members'], {
            queryParams: {
                id: this.ecNetwork.network.root.id
            },
            queryParamsHandling: 'merge'
        }).then();
    }

    onClickShowPods() {
        this.dialogService.open<any, any>({template: this.showPodsDialog}).closed.then();
    }

    getVoltage(voltage: VoltageLevel) {
        const findVoltage = POD_VOLTAGES.find((el) => el.value === voltage);
        return findVoltage ? findVoltage.name : null;
    }

    getPhase(phase: Phases) {
        const findPhase = POD_PHASES.find((el) => el.value === phase);
        return findPhase ? findPhase.name : null;
    }

    getPlantPredictiveModelType(type: ModelType): string {
        switch (type) {
            case ModelType.WIND:
                return 'Wind';
            case ModelType.PV:
            default:
                return 'Photovoltaic';
        }
    }

    /**
     * load representative data if needed
     * @param networkId networkId
     * @private
     */
    private loadRepresentative(networkId: string) {
        // load representative data startup
        if (!this.representative) {
            if (this.auth.profile.scopes?.includes(Scope.EMS)) {
                if (networkId) {
                    this.apiService.findRepresentativeByNetwork(networkId).subscribe(rep => {
                        return this.representative = {
                            representative: rep,
                            name: this.representativeToString(rep),
                            show: true
                        };
                    });
                }
            } else {
                this.representative = {show: false};
            }
        }
    }

    private loadNetwork(networkId: string, selectedResourceId?: string, forceReload: boolean = true): Promise<void> {
        // `networkId` is assumed to refer to an energy community
        if (!networkId) {
            this.ecNetwork = null;
            return Promise.resolve();
        }
        // employ easy "local" caching (network already loaded)
        if (!forceReload && !!this.ecNetwork && networkId === this.ecNetwork.network.root.id) {
            this.ecNetwork = {
                network: this.ecNetwork.network,
                selectedResourceId
            };
            return Promise.resolve();
        }
        return this.networkService.buildECNetwork(networkId).toPromise()
            .then(network => {
                this.ecNetwork = {
                    network: network as EnergyCommunityNetwork,
                    selectedResourceId
                };
                this.planningNetwork = {
                    networkId,
                    name: this.ecNetwork.network.root.name,
                    plannable: this.ecNetwork.network.rootEntity?.planning_cfg?.plannable,
                    operatable: can(this.ecNetwork.network.root.permissions, Permission.OPERATE)
                };
            })
            .then(() => this.buildStatistics());
    }

    private loadResource(resourceId: string): Promise<void> {
        if (!resourceId) {
            this.resource = null;
            this.aggregations = this.mkAggregations();
            return Promise.resolve();
        }
        return forkJoin([
            this.hierarchyService.findEntityById(resourceId).pipe(first()),
            this.apiService.getEntity(resourceId) // ask API for resource details
        ]).pipe(
            map(([he, e]) => ({...he, data: e} as Resource)),
            switchMap(res => this.getECMembers(res).pipe(
                map(members => ({...res, ...members} as Resource))
            ))
        ).toPromise()
            .then(resource => {
                this.resource = resource;
                this.forecastResource = {
                    resourceId: this.resource.id,
                    name: this.resource.name,
                    ecResource: this.resource.type === EntityType.POD ? this.ecNetwork.network.pods[this.resource.id] : null
                };
                this.aggregations = this.mkAggregations();
            });
    }

    private getResourceCommunityId(resourceId: string): Observable<string | null> {
        // employ easy "local" caching (resource is already part of current network)
        if (!!this.ecNetwork && resourceId in this.ecNetwork.network.entities) {
            return of(this.ecNetwork.network.root.id);
        }
        return this.apiService.getNetworkOfResource(resourceId).pipe(
            map(networkInfo => {
                if (networkInfo?.type !== SmagEntityNetworkType.ENERGY_COMMUNITY) {
                    throw new Error(`Bad type ${networkInfo.type} for network ${networkInfo.network_id}`);
                }
                return networkInfo.network_id;
            }),
            catchError(err => {
                console.error(`Resource ${resourceId} is not part of an energy community`, err);
                return of(null);
            })
        );
    }

    onPodUpserted(pod: EnergyCommunityNamedPod): Promise<void> {
        return this.hierarchyService.reloadHierarchy()
            .then(h => this.loadNetwork(this.ecNetwork.network.root.id, pod.id, true)) // select resource (EC is still valid)
            .then(() => this.loadResource(pod.id))
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate(mkErrorMessage(err, this.i18n.translate('Cannot add resource {0}', pod.name))), err);
                throw err;
            });
    }

    hasAbsorption(pod: TurboObjectWithDates<Pod>): boolean {
        return !!pod.statistical_profile.absorbed_monthly_energies;
    }

    hasInjection(pod: TurboObjectWithDates<Pod>): boolean {
        return !!pod.statistical_profile.injected_monthly_energies;
    }

    getYearlyEstimatedAbsorption(statisticalProfile: PodStatisticalProfile): number {
        return getStatisticalProfileTotalsFor(statisticalProfile.rate_mode, statisticalProfile.absorbed_monthly_energies);
    }

    getYearlyEstimatedInjection(statisticalProfile: PodStatisticalProfile): number {
        return getStatisticalProfileTotalsFor(statisticalProfile.rate_mode, statisticalProfile.injected_monthly_energies);
    }

    getYearlyEstimatedProduction(statisticalProfile: PlantStatisticalProfile): number {
        return getStatisticalProfileTotalsFor(statisticalProfile.rate_mode, statisticalProfile.produced_monthly_energies);
    }

    private deletePod(): Promise<void> {
        const name = this.resource.name;
        const networkId = this.ecNetwork.network.root.id;
        return this.apiService.deletePod(networkId, this.resource.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Resource {0} deleted', name));
                return undefined;
            })
            .then(() => this.hierarchyService.reloadHierarchy().then<void>())
            .then(() => this.loadNetwork(networkId, networkId, true)) // select EC root
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot delete resource {0}', name), err);
            });
    }

    private mkAggregations(): ECAggregation[] {
        switch (this.resource?.type) {
            case EntityType.ENERGY_COMMUNITY:
                return this.mkEnergyCommunityAggregations();
            case EntityType.POD:
                return this.mkPodAggregations();
            case EntityType.PLANT:
                return this.mkPlantAggregations();
            case EntityType.ACCUMULATOR:
                return this.mkStorageAggregations();
            default:
                return [];
        }
    }

    private mkEnergyCommunityAggregations(): ECAggregation[] {
        return [
            {
                name: 'Generated energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Effective injected energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Consumed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Absorbed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Self-consumed energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Self-consumption ratio',
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION, TimeAggregationType.SUM, timeRange),
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                ]).pipe(
                    map(([selfConsumedEnergy, generatedEnergy]) => {
                        const ratio = safeRatio(selfConsumedEnergy, generatedEnergy);
                        return isDefined(ratio) ? ratio * 100 : null;
                    })
                ),
                uomProvider: () => of('%')
            },
            {
                name: 'Shared energy',
                valueProvider: timeRange => this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY, TimeAggregationType.SUM, timeRange),
                uomProvider: () => of('kWh')
            },
            {
                name: 'Shared energy ratio',
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY, TimeAggregationType.SUM, timeRange),
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION, TimeAggregationType.SUM, timeRange),
                ]).pipe(
                    map(([sharedEnergy, injectedEnergy]) => {
                        const ratio = safeRatio(sharedEnergy, injectedEnergy);
                        return isDefined(ratio) ? ratio * 100 : null;
                    })
                ),
                uomProvider: () => of('%')
            },
            {
                name: 'Saved {0}',
                nameArgs: ['CO₂'],
                valueProvider: timeRange => forkJoin([
                    this.loadPastAggregationData(EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION, TimeAggregationType.SUM, timeRange),
                    this.configurationService.economicsConfig.pipe(first()),
                ]).pipe(
                    map(([generatedEnergy, ecoCfg]) => computeSavedCO2(generatedEnergy, ecoCfg))
                ),
                uomProvider: () => of('t')
            }
        ];
    }

    private mkPodAggregations(): ECAggregation[] {
        const ecPod = this.ecNetwork.network.pods[this.resource.id];
        if (!ecPod) {
            return [];
        }
        const aggregations: ECAggregation[] = [];
        if (isConsumer(ecPod) || isProsumer(ecPod)) {
            aggregations.push({
                    name: 'Absorbed energy',
                    valueProvider: timeRange => this.loadDeviceData(POD_ABSORBED_ENERGY_DEVICE, TimeAggregationType.SUM, timeRange),
                    uomProvider: () => this.loadDeviceUom(POD_ABSORBED_ENERGY_DEVICE, 'kWh')
                },
                {
                    name: 'Average absorbed power',
                    valueProvider: timeRange => this.loadDeviceData(POD_AVERAGE_ABSORBED_POWER_DEVICE, TimeAggregationType.MEAN, timeRange),
                    uomProvider: () => this.loadDeviceUom(POD_AVERAGE_ABSORBED_POWER_DEVICE, 'kW')
                });

        }
        if (isProducer(ecPod) || isProsumer(ecPod)) {
            aggregations.push({
                    name: 'Effective injected energy',
                    valueProvider: timeRange => this.loadDeviceData(POD_INJECTED_ENERGY_DEVICE, TimeAggregationType.SUM, timeRange),
                    uomProvider: () => this.loadDeviceUom(POD_INJECTED_ENERGY_DEVICE, 'kWh')
                },
                {
                    name: 'Average injected power',
                    valueProvider: timeRange => this.loadDeviceData(POD_AVERAGE_INJECTED_POWER_DEVICE, TimeAggregationType.MEAN, timeRange),
                    uomProvider: () => this.loadDeviceUom(POD_AVERAGE_INJECTED_POWER_DEVICE, 'kW')
                });
        }
        return aggregations;
    }

    private mkPlantAggregations(): ECAggregation[] {
        return [
            {
                name: 'Produced energy',
                valueProvider: timeRange => this.loadDeviceData(PLANT_PRODUCED_ENERGY_DEVICE, TimeAggregationType.SUM, timeRange),
                uomProvider: () => this.loadDeviceUom(PLANT_PRODUCED_ENERGY_DEVICE, 'kWh')
            },
            {
                name: 'Average produced power',
                valueProvider: timeRange => this.loadDeviceData(PLANT_AVERAGE_PRODUCED_POWER_DEVICE, TimeAggregationType.MEAN, timeRange),
                uomProvider: () => this.loadDeviceUom(PLANT_AVERAGE_PRODUCED_POWER_DEVICE, 'kW')
            }
        ];
    }

    private mkStorageAggregations(): ECAggregation[] {
        return [
            {
                name: 'Absorbed energy',
                valueProvider: timeRange => this.loadDeviceData(ACCUMULATOR_ABSORBED_ENERGY_DEVICE, TimeAggregationType.SUM, timeRange),
                uomProvider: () => this.loadDeviceUom(ACCUMULATOR_ABSORBED_ENERGY_DEVICE, 'kWh')
            },
            {
                name: 'Average absorbed power',
                valueProvider: timeRange => this.loadDeviceData(ACCUMULATOR_AVERAGE_ABSORBED_POWER_DEVICE, TimeAggregationType.MEAN, timeRange),
                uomProvider: () => this.loadDeviceUom(ACCUMULATOR_AVERAGE_ABSORBED_POWER_DEVICE, 'kW')
            },
            {
                name: 'Injected energy',
                valueProvider: timeRange => this.loadDeviceData(ACCUMULATOR_INJECTED_ENERGY_DEVICE, TimeAggregationType.SUM, timeRange),
                uomProvider: () => this.loadDeviceUom(ACCUMULATOR_INJECTED_ENERGY_DEVICE, 'kWh')
            },
            {
                name: 'Average injected power',
                valueProvider: timeRange => this.loadDeviceData(ACCUMULATOR_AVERAGE_INJECTED_POWER_DEVICE, TimeAggregationType.MEAN, timeRange),
                uomProvider: () => this.loadDeviceUom(ACCUMULATOR_AVERAGE_INJECTED_POWER_DEVICE, 'kW')
            },
            {
                name: 'State of charge',
                valueProvider: timeRange => this.loadDeviceData(ACCUMULATOR_STATE_OF_CHARGE_DEVICE, TimeAggregationType.MEAN, timeRange).pipe(
                    map(value => isDefined(value) ? value * 100 : null)
                ),
                uomProvider: () => this.loadDeviceUom(ACCUMULATOR_STATE_OF_CHARGE_DEVICE, '%')
            }
        ];
    }

    private loadDeviceData(deviceKey: string,
                           timeAggregation: TimeAggregationType,
                           timeRange: [number, number]): Observable<number> {
        const [startTime, endTime] = timeRange;

        return this.apiService.aggregateMeasures(this.resource.id,
            [deviceKey],
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `60 ${TIME_UNITS.MINUTES}`, // always 1 hour minutes for ECs
            timeAggregation,
            MeasureAggregationDataSource.CASCADE,
            true).pipe(
            map(resp => resp.values[deviceKey]?.length ? resp.values[deviceKey][0] : null)
        );
    }

    private loadDeviceUom(deviceKey: string, defaultUom?: string): Observable<string> {
        if (hasFieldEntityDevice(this.resource.data, deviceKey)) {
            return this.apiService.getConnectorDevice((this.resource.data.connector_devices || {})[deviceKey])
                .pipe(
                    map(d => d.uom || defaultUom || null),
                    catchError(e => {
                        // fall back on supplied default measure if the device is not found
                        if (errorCode(e) === ErrorCode.DEVICE_NOT_FOUND) {
                            return of(defaultUom || null);
                        }
                        return throwError(e);
                    })
                );
        }
        return of(defaultUom || null);
    }

    private loadPastAggregationData(aggregationType: EntityMeasureAggregationType,
                                    timeAggregation: TimeAggregationType,
                                    timeRange: [number, number]): Observable<number> {
        let [startTime, endTime] = timeRange;
        // we round to the nearest hour to comply with economics
        startTime = startTime - (startTime % 3600000);
        endTime = endTime - (endTime % 3600000);

        return this.apiService.aggregateEntityMeasures(this.resource.id,
            aggregationType,
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `60 ${TIME_UNITS.MINUTES}`, // always 1 hour minutes for ECs
            timeAggregation,
            MeasureAggregationDataSource.CASCADE,
            true,
            true).pipe(
            map(agg => agg.values.length ? agg.values[0] : null)
        );
    }

    private getECMembers(resource: Resource): Observable<{ members: ECMember[], holder?: ECMember, owner?: ECMember }> {
        let pod: string = null;
        if (resource.type === EntityType.PLANT) {
            pod = this.ecNetwork.network.pods[resource.parent.id].pod;
        } else if (resource.type === EntityType.POD) {
            pod = this.ecNetwork.network.pods[resource.id].pod;
        }
        if (pod) {
            return this.apiService.findMembersByPOD(pod).pipe(
                map(members => {
                    const podRelations =
                        _.fromPairs(members.results.map(member => [member.id, getMemberTargetPodRelations(member.relations, pod)]));
                    const {holder, owner} = getPodCurrentHolderAndOwner(podRelations);
                    return {
                        members: members.results,
                        holder: holder ? members.results.find(member => member.id === holder.memberId) : undefined,
                        owner: owner ? members.results.find(member => member.id === owner.memberId) : undefined
                    };
                })
            );
        }
        return of({members: []});
    }

    private representativeToString(rep: ECRepresentative) {
        return rep ? `${rep.personal_data.first_name} ${rep.personal_data.last_name}` : '';
    }

    public asEnergyCommunity(entity: Entity): TurboObjectWithDates<EnergyCommunity> {
        return entity as TurboObjectWithDates<EnergyCommunity>;
    }

    private buildStatistics() {
        const network = this.ecNetwork?.network;
        if (network) {
            const pods = Object.keys(network.pods).length;
            const plants = Object.keys(network.pods)
                .map(key => network.pods[key].plants)
                .map(podPlants => Object.keys(podPlants).length)
                .reduce((a, b) => a + b, 0);
            this.statistics.pods = pods;
            this.statistics.plants = plants;
        }
    }

    onClickEditCommunity() {
        this.dialogService.open<any, void>({
            title: this.i18n.translate('Configuration'),
            template: this.communityEditorDialog,
            context: {
                community: _.cloneDeep(this.resource.data),
                hierarchyEntity: this.resource
            }
        });
    }

    onClickUpdateCommunity(req: UpdateEnergyCommunityRequest, closeFn: () => void) {
        if (req) {
            this.updateCommunity(req).then(() => closeFn());
        } else {
            closeFn();
        }
    }


    private updateCommunity(req: UpdateEnergyCommunityRequest): Promise<void> {
        const reloadHierarchy = req.name !== this.resource.name;
        return this.apiService.updateEnergyCommunity(this.resource.id, req)
            .then(() => reloadHierarchy ? this.hierarchyService.reloadHierarchy().then(() => undefined) : Promise.resolve(undefined))
            .then(() => this.loadNetwork(this.ecNetwork.network.root.id, this.resource.id, true))
            .then(() => {
                // update parameters
                const ec = this.resource.data as EnergyCommunity;
                ec.address = req.address;
                ec.spatial_coordinates = req.spatial_coordinates;
                ec.zone = req.zone;
                ec.activation_date = req.activation_date;
                // this.resource.name = name;
                this.msgBoxService.info(this.i18n.translate('Energy Community successfully updated'));
            })
            .catch(err => {
                // EntityParametersComponent still needs to be "reset" to old values
                this.msgBoxService.error(this.i18n.translate('An error occurred while updating energy community'), err);
            });
    }

    onClickDeleteCommunity() {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Delete {0}', this.npp.transform(this.resource.path)),
            template: this.deleteCommunityDialog
        }).closed.then(del => {
            if (del) {
                this.deleteCommunity().then();
            }
        });
    }

    private deleteCommunity() {
        const name = this.resource.name;
        return this.apiService.deleteEnergyCommunity(this.resource.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Community {0} deleted', name));
            })
            .then(() => this.hierarchyService.reloadHierarchy())
            .then(() => this.router.navigate(['/c', 'dev-dashboard'])) // navigate to developer dashboard
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot delete community {0}', name), err);
            });
    }
}

function computeSavedCO2(generatedEnergy: number, ecoCfg: EconomicsConfig) {
    return isDefined(generatedEnergy) ? generatedEnergy * ecoCfg.co2_E_ef / 1000000 : null;
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.POD_CONNECTED_TO_NEW_ENTITY_NOT_IN_COMMUNITY:
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects resource to a member that is not part of this Energy Community';
        default:
            return fallback;
    }
}
